import { NgClass } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, Component, OnDestroy, effect, inject, input, output, signal } from '@angular/core';
import { IonSpinner } from '@ionic/angular/standalone';
import { VxImageComponent } from '@libs/core/lib/shared/mtjr-image.component';
import { LanguageSwitcherStore } from '@libs/mtjr/shared/i18n/language-switcher.store';
import { Swiper } from 'swiper/types';

@Component({
  selector: 'mtjr-product-swiper',
  standalone: true,
  imports: [VxImageComponent, NgClass, IonSpinner],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <div class="w-max-mid w-full">
      @if (images().length > 0) {
        <swiper-container (swiperinit)="onSwiperInit($any($event))">
          @for (url of images(); track $index) {
            <swiper-slide>
              <div class="items-center justify-center" [ngClass]="{ 'flex pt-4': isVoucherVendor() }">
                @if (isVoucherVendor()) {
                  <mtjr-img
                    class="w-full"
                    proxy="https://dev-img.mtjr.ly/unsafe/resize:auto:1280:720/sm:1/q:85/plain/"
                    [src]="url"
                    alt="product-image"
                    [disabled]="true"
                    [imgClass]="
                      'h-[250px] w-full rounded-b-3xl' + (isVoucherVendor() ? ' object-contain' : ' object-cover')
                    "
                  />
                } @else {
                  <mtjr-img
                    class="w-full"
                    proxy="https://dev-img.mtjr.ly/unsafe/resize:auto:1280:720/sm:1/q:85/plain/"
                    [src]="url"
                    alt="product-image"
                    [imgClass]="
                      'h-[250px] w-full rounded-b-3xl' + (isVoucherVendor() ? ' object-contain' : ' object-cover')
                    "
                    [blurHash]="blurHash()"
                  />
                }
              </div>
            </swiper-slide>
          }
        </swiper-container>
        @if (images().length > 1) {
          <div
            class="bg-medium/15 flex gap-1 overflow-x-scroll px-1 py-1 md:grid md:grid-cols-5 md:rounded-md"
            style="scrollbar-width: none; -ms-overflow-style: none"
          >
            @for (url of images(); track $index) {
              <img
                [src]="url"
                class="aspect-square w-16 rounded-md object-cover md:w-auto"
                [ngClass]="{ 'border-2 border-solid border-blue-500': $index === swiperActiveIndex() }"
                alt="product-image"
                (click)="swiper()?.slideTo($index)"
              />
            }
          </div>
        }
      } @else {
        <div class="bg-light/50 flex h-[250px] w-full flex-1 items-center justify-center">
          <ion-spinner name="circles" class="text-primary" />
        </div>
      }
    </div>
  `,
})
export class VxProductSwiperComponent implements OnDestroy {
  images = input.required<string[]>();
  isVoucherVendor = input<boolean>(false);
  swiperInit = output<Swiper>();
  activeIndexChange = output<number>();
  blurHash = input<string | undefined | null>();

  swiper = signal<Swiper | undefined>(undefined);
  swiperActiveIndex = signal(0);
  private langStore = inject(LanguageSwitcherStore);

  constructor() {
    effect(() => {
      this.activeIndexChange.emit(this.swiperActiveIndex());
    });
  }

  onSwiperInit(event: { detail: [Swiper] }) {
    const [swiper] = event.detail;
    this.swiper.set(swiper);
    swiper.changeLanguageDirection(this.langStore.currentLang()?.dir ?? 'ltr');
    swiper.on('slideChange', () => {
      this.swiperActiveIndex.set(swiper.activeIndex);
    });
    this.swiperInit.emit(swiper);
  }

  slideTo(index: number) {
    this.swiper()?.slideTo(index);
  }

  getSwiperInstance(): Swiper | undefined {
    return this.swiper();
  }

  ngOnDestroy() {
    this.swiper()?.destroy();
  }
}
